import { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ViewListIcon from '@mui/icons-material/ViewList';
import StarIcon from '@mui/icons-material/Star';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useDeleteRoute } from '../hooks/useDeleteRoute';
import { useSetCurrentRoute } from '../hooks/useSetCurrentRoute';

interface Props {
  anchorEl: HTMLElement | null;
  routeId: string | null;
  onClose: () => void;
  onShowProducts: (routeId: string) => void;
}

export default function RouteContextMenu({ anchorEl, routeId, onClose, onShowProducts }: Props) {
  const navigate = useNavigate();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const deleteRoute = useDeleteRoute();
  const setCurrentRoute = useSetCurrentRoute();

  function handleMakeActive() {
    if (routeId) {
      setCurrentRoute.mutate(routeId);
    }
    onClose();
  }

  /** Navigate to the route details (products on route) page */
  function handleShowProducts() {
    if (routeId) onShowProducts(routeId);
    onClose();
  }

  function handleEdit() {
    if (routeId) navigate(`/routes/${routeId}/edit`);
    onClose();
  }

  function handleDelete() {
    if (routeId) setConfirmDeleteId(routeId);
    onClose();
  }

  function handleConfirmDelete() {
    if (confirmDeleteId) {
      deleteRoute.mutate(confirmDeleteId);
    }
    setConfirmDeleteId(null);
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={onClose}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <MenuItem onClick={handleShowProducts}>
          <ListItemIcon>
            <ViewListIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Show products</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMakeActive} disabled={setCurrentRoute.isPending}>
          <ListItemIcon>
            <StarIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Make active</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={onClose}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clone</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Route</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this route? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteRoute.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
